import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import AuthCallback from "@/pages/auth/AuthCallback";
import MemberDashboard from "@/pages/dashboard/MemberDashboard";
import WalletStatementPage from "@/pages/dashboard/WalletStatementPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SystemAdmin from "@/pages/system/SystemAdmin";
import InstallPrompt from "@/components/InstallPrompt";
import { isAdmin, isSuperAdmin } from "@/lib/roles";

function ProtectedRoute({ children, requireAdmin, requireSuperAdmin }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse space-y-3 w-48">
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded w-1/2" />
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (requireSuperAdmin && !isSuperAdmin(user)) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center gap-4">
      <h1 className="font-heading text-2xl font-bold">Super Admin Only</h1>
      <p className="text-muted-foreground text-sm max-w-xs">This area is restricted to System/Super Admins.</p>
      <a href="/" className="text-primary underline text-sm">Go to dashboard</a>
    </div>
  );
  if (requireAdmin && !isAdmin(user)) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center gap-4">
      <h1 className="font-heading text-2xl font-bold">Access Denied</h1>
      <p className="text-muted-foreground text-sm max-w-xs">You don't have admin privileges.</p>
      <a href="/" className="text-primary underline text-sm">Go to dashboard</a>
    </div>
  );
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse space-y-3 w-48">
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-3/4" />
      </div>
    </div>
  );
  if (user) return <Navigate to={isAdmin(user) ? "/admin" : "/dashboard"} replace />;
  return children;
}

// CRITICAL: Check URL fragment for session_id synchronously during render
function AppRouter() {
  const location = useLocation();
  
  // Handle Google OAuth callback - check for session_id in hash
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/wallet" element={<ProtectedRoute><WalletStatementPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path="/system" element={<ProtectedRoute requireSuperAdmin><SystemAdmin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AppRouter />
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
